import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-foreground-600">未找到该文章</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.categoryName, to: `/category/dianzi-youxi` },
            { label: article.title },
          ]}
        />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> 阅读 {article.readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - PG游戏攻略`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享：</span>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    categoryName={ra.categoryName}
                    author={ra.author}
                    date={ra.date}
                    readTime={ra.readTime}
                    image={ra.image}
                    tags={ra.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: 'PG游戏官方平台',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.pggame-official.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: 'https://www.pggame-official.com/category/dianzi-youxi',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}